import { computed } from 'vue';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { storeToRefs } from 'pinia';
import { useJssStore } from '../../stores/jssStore';

export const DEFAULT_DATA_LAYER = 'DEFAULT_DATA_LAYER' as const;
export const ECOMMERCE_DATA_LAYER_ITEM = 'ECOMMERCE_DATA_LAYER_ITEM' as const;
export const VENUE_FINDER_DATA_LAYER = 'VENUE_FINDER_DATA_LAYER' as const;
export const BOOKING_DATA_LAYER_ITEM = 'BOOKING_DATA_LAYER_ITEM' as const;

export type DataLayerType =
  | typeof DEFAULT_DATA_LAYER
  | typeof ECOMMERCE_DATA_LAYER_ITEM
  | typeof VENUE_FINDER_DATA_LAYER
  | typeof BOOKING_DATA_LAYER_ITEM;

export interface DataLayerDefaultProps {
  defaultGlobalProps?: Record<string, unknown>;
  defaultItemProps?: Record<string, unknown>;
  defaultEcommerceProps?: Record<string, unknown>;
}

export const useDataLayerDefaultProps = (dataLayerType: DataLayerType): DataLayerDefaultProps => {
  const jssStore = useJssStore();
  const {
    venueName,
    venueId,
    venueOrigin,
    itemPath,
    canonicalUrl,
    brandName,
    segment,
    subDivision,
    subSegment,
    siteVirtualFolder,
  } = storeToRefs(jssStore);

  const path = computed(() => siteVirtualFolder.value?.split('/') || []);
  const fullUrl = computed(() =>
    isServer() ? canonicalUrl.value : window.location.href.split('?')[0]
  );

  const dataLayerDefaultProps: Record<DataLayerType, DataLayerDefaultProps> = {
    [DEFAULT_DATA_LAYER]: {
      defaultGlobalProps: {
        event: 'basic_event',
        pub: venueName.value && venueId.value ? `${venueName.value}-${venueId.value}` : null,
        link_page_path: itemPath.value,
        link_domain: venueOrigin.value,
      },
    },
    [ECOMMERCE_DATA_LAYER_ITEM]: {
      defaultItemProps: {
        location_id: fullUrl.value,
      },
    },
    [VENUE_FINDER_DATA_LAYER]: {
      defaultGlobalProps: {
        event: 'basic_event',
        link_page_path: itemPath.value,
        link_domain: venueOrigin.value,
      },
    },
    [BOOKING_DATA_LAYER_ITEM]: {
      defaultGlobalProps: {
        userId: '',
        _userProject: 'BookingApp',
        _sub_division: subDivision.value,
        _sub_brand_segment: subSegment.value,
      },
      defaultEcommerceProps: {
        coupon: '',
        _currency: 'GBP',
        payment_type: '',
        tax: 0,
        value: 0,
      },
      defaultItemProps: {
        additional_requirements: false,
        _affiliation: venueName.value,
        _brand: brandName.value,
        _currency: 'GBP',
        coupon: '',
        disabled_access: null,
        discount: 0,
        highchair_requested: 0,
        index: 0,
        _item_brand: segment.value,
        item_id: venueId.value,
        item_list_name: 'no_search_performed',
        item_variant: null,
        item_category2: path.value?.[1],
        item_category3: path.value?.[2],
        item_category4: path.value?.[3],
        price: 0,
        _pub_name: venueName.value,
        venue_id: venueId.value,
        quantity: 1,
      },
    },
  };

  return dataLayerDefaultProps?.[dataLayerType] || {};
};
